import { Button, FormControl, FormControlLabel, FormLabel, Grid, Paper, Radio, RadioGroup, Switch, Tooltip } from "@material-ui/core";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import CustomInput from "components/CustomInput";
import DropdownFilter from "components/DropdownFilter";
import NotificationProvider from "components/NotificationProvider";
import { useGetAllActivityItemsQuery } from "features/activity/activitySlice";
import { useGetAllBundlesQuery } from "features/activityBundle/activityBundleSlice";
import { selectTenantName } from "features/user/userSlice";
import { useGetVehicleTypesQuery } from "features/vehicles/vehicleSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import usePrompt from "hooks/usePrompt";
import { IconDetailing, IconFuel, IconInspect, IconMechanic, IconOil, IconTire } from "icons";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { arrayToObject } from "utils/object-util";
import { LOT_OPTIONS, normalizeServerRule, mapAppliesToToServerLotOptions } from "features/pricingManagement/utils/lotOptionsMapper";
import { ruleFieldType, ruleFieldTypeConfig } from "../../consts";
import { useAddMechanicalRuleMutation, useDeleteMechanicalsConfigMutation, useGetMechanicalRuleTypesQuery, useUpdateMechanicalRuleMutation } from "../../mechanicalsSlice";
import { lotMechanicalConfigStyles } from "./style";
import ActivityTypesField from "features/activity/ActivityTypesField";

const Rule = ({
    rule,
    ruleIndex,
    onCancel,
    selectLot,
    clientLots,
    presetActivityId = null,
    presetActivityData = null,
    showLotInfo,
    mechanicalsRulesToEdit,
    setMechanicalsRulesToEdit,
    clientsWithConfigs = null,
    allowClientSelection = false,
    onUnsavedChanges = null,
    hideVendor = false
}) => {
    const classes = lotMechanicalConfigStyles();
    const isNewActivityMode = !!presetActivityData;
    const clientsWithConfigsLookup = useMemo(() => arrayToObject(clientsWithConfigs), [clientsWithConfigs]);

    const getClientLots = (clientId) => {
        if (!clientsWithConfigs || !clientId) return [];
        const clientConfig = clientsWithConfigsLookup[clientId];
        return clientConfig?.clientLots?.map(c => ({ id: c.lotId, name: c.name })) || [];
    };

    const [currentClientLots, setCurrentClientLots] = useState([]);

    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: bundles, error: bundlesError, isFetching: isLoadingBundles } = useGetAllBundlesQuery();
    let { data: vehicleTypes, error: vehicleTypesError, isLoading: isLoadingVehicleTypes } = useGetVehicleTypesQuery();
    let { data: mechanicalRuleTypes, error: mechanicalRuleTypesError, isFetching: isLoadingMechanicalRuleTypes } = useGetMechanicalRuleTypesQuery();//TODO only mechanicals
    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetVendorsQuery();

    let [updateMechanicalRule, { isLoading: isSaving }] = useUpdateMechanicalRuleMutation();
    let [deleteRule, { isLoading: isDeleting }] = useDeleteMechanicalsConfigMutation();
    let [addRule, { isLoading: isAdding }] = useAddMechanicalRuleMutation();

    const tenantName = useSelector(selectTenantName);
    const { triggerPrompt } = usePrompt();
    const [showLotDropdown, setShowLotDropdown] = useState(false);

    const activityItemsLookup = arrayToObject(activityItems);

    const ruleOperands = [
        {
            id: 1,
            name: 'And',
        },
        {
            id: 2,
            name: 'Or',
        }
    ]

    const ruleIcons = {
        'oil': <IconOil />,
        'fuel': <IconFuel />,
        'tire': <IconTire />,
        'inspection': <IconInspect />,
        'detailing': <IconDetailing />,
        DEFAULT: <IconMechanic />
    }

    const timeFrameTypeValues = [
        { id: 1, name: 'Absolute' },
        { id: 2, name: 'Relative to last completion' }
    ]

    const bundlesLookup = arrayToObject(bundles);

    const getIcon = (activityId, activityBundleId) => {
        if (activityBundleId || (!activityId && !presetActivityData)) {
            return ruleIcons.DEFAULT;
        }
        const parsedName = activityItemsLookup?.[activityId]?.name.split(' ')[0].toLowerCase();
        const icon = ruleIcons[parsedName];
        return icon ?? ruleIcons.DEFAULT
    }

    const getDisplayName = () => {
        // If rule has a bundle ID, display the bundle name
        if (ruleToEdit.bundleId) {
            return bundlesLookup?.[ruleToEdit.bundleId]?.name || 'Unknown Bundle';
        }

        // If activityId is null or 'new' (new activity being created) and we have presetActivityData, use the activity name from there
        if ((!ruleToEdit.activityId || ruleToEdit.activityId === 'new') && presetActivityData?.name) {
            return presetActivityData.name;
        }

        // Otherwise, display the activity name
        return activityItemsLookup?.[ruleToEdit.activityId]?.name || 'Unknown Activity';
    }

    const [ruleToEdit, setRuleToEdit] = useState({ ...rule });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(rule.isNew || false);
    const [activityDropdownValue, setActivityDropdownValue] = useState(null);

    useEffect(() => {
        if (allowClientSelection) {
            setCurrentClientLots(getClientLots(ruleToEdit?.clientId));
            return;
        }

        setCurrentClientLots(clientLots?.map(c => ({ id: c.lotId, name: c.name })) || []);
    }, [allowClientSelection, clientsWithConfigsLookup, ruleToEdit?.clientId, clientLots]);

    const setUnsavedChanges = (val) => {
        setHasUnsavedChanges(val);
        onUnsavedChanges?.(val);
    };

    useEffect(() => {
        const updatedRule = normalizeServerRule(rule);

        setRuleToEdit(updatedRule);
        setUnsavedChanges(rule.isNew || false);

        // Initialize dropdown value for existing rules
        if (!rule.isNew) {
            if (rule.bundleId) {
                // It's a bundle
                const jsonValue = JSON.stringify({ activityItemId: null, bundleId: rule.bundleId, activityTypeId: null, isBundle: true });
                setActivityDropdownValue(jsonValue);
            } else if (rule.activityId) {
                // It's an activity
                const activityItem = activityItemsLookup?.[rule.activityId];
                if (activityItem) {
                    const jsonValue = JSON.stringify({ activityItemId: rule.activityId, bundleId: null, activityTypeId: activityItem.activityTypeId, isBundle: false });
                    setActivityDropdownValue(jsonValue);
                }
            }
        }

        // Update showLotDropdown state
        if (updatedRule.appliesTo === LOT_OPTIONS.SPECIFIC_LOTS) {
            setShowLotDropdown(true);
        }
    }, [rule, activityItems]);

    const saveRuleToParent = (updatedRule) => {
        if (isNewActivityMode && setMechanicalsRulesToEdit && ruleIndex !== undefined) {
            const newRules = [...mechanicalsRulesToEdit];
            newRules[ruleIndex] = updatedRule;
            setMechanicalsRulesToEdit(newRules);
        }
    };

    const removeRuleFromParent = () => {
        if (isNewActivityMode && setMechanicalsRulesToEdit && ruleIndex !== undefined) {
            const newRules = mechanicalsRulesToEdit.filter((_, index) => index !== ruleIndex);
            setMechanicalsRulesToEdit(newRules);
        }
    };

    const onFieldChange = (field, val) => {
        const updatedRule = { ...ruleToEdit, [field]: val };
        setRuleToEdit(updatedRule);
        setUnsavedChanges(true);

        // Update lots when client changes
        if (field === 'clientId' && allowClientSelection) {
            // Clear selected lots when client changes
            updatedRule.lotId = null;
            setRuleToEdit(updatedRule);
        }
    }

    const onRuleParamChange = (val, type, subType) => {
        let params = [...(ruleToEdit?.ruleParams ?? [])]
        let index = params.findIndex(p => p.paramType === type && p?.paramSubType === subType)
        if (index === -1) {
            params = [...params, { ['paramType']: type, ['paramSubType']: subType, ['paramValue']: val, ['ruleId']: ruleToEdit?.id }]
        }
        else {
            params[index] = { ...params[index], ['paramValue']: val };
        }
        const updatedRule = { ...ruleToEdit, ['ruleParams']: params };
        setRuleToEdit(updatedRule);
        setUnsavedChanges(true);
    }

    const onChangeVehicleTypes = (val) => {
        if (val[val.length - 1] == null && val.length !== 0) {
            val = [null];
            onFieldChange("vehicleTypeIdsList", val);
        }
        else {
            if (ruleToEdit.vehicleTypeIdsList?.includes(null)) {
                val = val.filter(v => v != null);
                onFieldChange("vehicleTypeIdsList", val);
            } else {
                onFieldChange("vehicleTypeIdsList", val);
            }
        }
    };

    const onSelectLots = (val) => {
        onFieldChange("lotId", val)
    }

    const onSave = async () => {
        let res;

        if (presetActivityData && ruleToEdit?.activityId === 'new') {
            onCancel();
            return;
        }
        if (ruleToEdit?.isNew) {
            const lotOptions = mapAppliesToToServerLotOptions(ruleToEdit?.appliesTo);
            res = await addRule({
                clientId: ruleToEdit.clientId,
                lotIds: ruleToEdit?.appliesTo == LOT_OPTIONS.SPECIFIC_LOTS || !ruleToEdit?.appliesTo ? (!Array.isArray(ruleToEdit.lotId) ? [ruleToEdit.lotId] : ruleToEdit.lotId) : null,
                activityId: presetActivityId ?? ruleToEdit.activityId,
                bundleId: ruleToEdit.bundleId,
                vehicleTypeIdsList: ruleToEdit?.vehicleTypeIdsList?.includes(null) ? null : ruleToEdit?.vehicleTypeIdsList,
                calcOnCheckin: ruleToEdit?.calcOnCheckin || false,
                ruleOperand: ruleToEdit?.ruleOperand || 1,
                daysToComplete: ruleToEdit?.daysToComplete,
                preferredVendorId: ruleToEdit?.preferredVendorId,
                ruleParams: ruleToEdit?.ruleParams?.filter(r => r?.paramValue) || [],
                ...lotOptions,
                timeFrameType: ruleToEdit?.timeFrameType
            })
        }
        else {
            var vehicleTypeIdsList = ruleToEdit?.vehicleTypeIdsList?.includes(null) ? null : ruleToEdit?.vehicleTypeIdsList;
            setRuleToEdit({ ...ruleToEdit, ['vehicleTypeIdsList']: vehicleTypeIdsList })
            res = await updateMechanicalRule(ruleToEdit);
        }
        if (!res?.error) {
            NotificationProvider.success('Successfully saved rule');
            onCancel();
        } else {
            NotificationProvider.error('Failed to save rule');
            setRuleToEdit({ ...rule });
        }
    }

    const onDelete = async () => {
        triggerPrompt({
            title: "Delete Rule",
            content: "Are you sure you want to delete the rule?",
            onConfirm: async () => {
                const res = await deleteRule({
                    id: ruleToEdit?.id
                })

                if (!res?.error) {
                    NotificationProvider.success('Successfully deleted rule');
                } else {
                    NotificationProvider.error('Failed to delete rule');
                }
            },
        });
    }

    const cancel = () => {
        if (ruleToEdit?.isNew) {
            onCancel();
        }
        else {
            setRuleToEdit(rule);
            setUnsavedChanges(false);

            // Reset dropdown value
            if (rule.bundleId) {
                const jsonValue = JSON.stringify({ activityItemId: null, bundleId: rule.bundleId, activityTypeId: null, isBundle: true });
                setActivityDropdownValue(jsonValue);
            } else if (rule.activityId) {
                const activityItem = activityItems?.find(a => a.id === rule.activityId);
                if (activityItem) {
                    const jsonValue = JSON.stringify({ activityItemId: rule.activityId, bundleId: null, activityTypeId: activityItem.activityTypeId, isBundle: false });
                    setActivityDropdownValue(jsonValue);
                }
            }

            if (rule.appliesTo === LOT_OPTIONS.SPECIFIC_LOTS || (!rule.appliesTo && !rule.allLots && !rule.allTenantLots)) {
                setShowLotDropdown(true);
            } else {
                setShowLotDropdown(false);
            }
        }
    }

    const allRuleConfigs = mechanicalRuleTypes?.filter(c => c.paramType != ruleFieldType.VEHICLE_TYPE);
    const vehicleTypeConfig = mechanicalRuleTypes?.filter(c => c.paramType === ruleFieldType.VEHICLE_TYPE);

    // Group configs by activityId
    const allRuleConfigsLookup = allRuleConfigs?.filter(obj => obj.activityId)?.reduce((map, obj) => {
        if (!map[obj.activityId]) {
            map[obj.activityId] = [];
        }
        map[obj.activityId].push(obj);
        return map;
    }, {});

    // Group configs by bundleId
    const bundleRuleConfigsLookup = allRuleConfigs?.filter(obj => obj.bundleId)?.reduce((map, obj) => {
        if (!map[obj.bundleId]) {
            map[obj.bundleId] = [];
        }
        map[obj.bundleId].push(obj);
        return map;
    }, {})

    const vehicleTypeConfigLookup = vehicleTypeConfig?.reduce(function (map, obj) {
        map[obj.activityId] = obj;
        return map;
    }, {})

    const getEffectiveRuleConfigs = () => {
        if (presetActivityData?.paramTypes) {
            return presetActivityData.paramTypes.filter(p => p.paramType !== ruleFieldType.VEHICLE_TYPE);
        }
        if (ruleToEdit?.bundleId) {
            return bundleRuleConfigsLookup?.[ruleToEdit?.bundleId] || [];
        }
        return allRuleConfigsLookup?.[ruleToEdit?.activityId] || [];
    };

    const getEffectiveVehicleTypeConfig = () => {
        if (presetActivityData?.paramTypes) {
            return presetActivityData.paramTypes.find(p => p.paramType === ruleFieldType.VEHICLE_TYPE);
        }
        return vehicleTypeConfigLookup?.[ruleToEdit?.activityId];
    };

    const effectiveRuleConfigs = getEffectiveRuleConfigs();
    const effectiveVehicleTypeConfig = getEffectiveVehicleTypeConfig();

    const onActivityChange = (val, metadata) => {
        let activityItemId = null;
        let bundleId = null;
        let ruleParams = [];

        if (metadata?.jsonValue) { //when includeBundles flag is true in dropdown
            const parsed = JSON.parse(metadata.jsonValue);
            activityItemId = parsed.activityItemId;
            bundleId = parsed.bundleId;

            if (!!bundleId) {
                ruleParams = bundleRuleConfigsLookup?.[bundleId] || [];
            } else if (activityItemId) {
                ruleParams = allRuleConfigsLookup?.[activityItemId] || [];
            }
        } else {
            activityItemId = val ? parseInt(val) : val;
            ruleParams = allRuleConfigsLookup?.[activityItemId] || [];
        }

        const updates = {
            activityId: activityItemId,
            bundleId: bundleId,
            vehicleTypeIdsList: null,
            calcOnCheckin: false,
            ruleOperand: 1,
            daysToComplete: null,
            preferredVendorId: null,
            ruleParams: ruleParams
        };

        // Store the jsonValue for the dropdown
        setActivityDropdownValue(metadata?.jsonValue || null);

        setRuleToEdit({
            ...ruleToEdit,
            ...updates
        });
        setUnsavedChanges(true);
    }

    const handleRadioChange = (event) => {
        const value = event.target.value;
        const lotOptions = mapAppliesToToServerLotOptions(value);
        const updates = {
            appliesTo: value,
            ...lotOptions
        };

        // Clear lotId when switching to "all lots" options
        if (value !== LOT_OPTIONS.SPECIFIC_LOTS) {
            updates.lotId = null;
        }

        setRuleToEdit({ ...ruleToEdit, ...updates });
        setUnsavedChanges(true);

        if (value === LOT_OPTIONS.SPECIFIC_LOTS) {
            setShowLotDropdown(true);
        } else {
            setShowLotDropdown(false);
        }
    };
    const showTimeFrameType = effectiveRuleConfigs?.find(c => c.paramType == ruleFieldType.WEEKS || c.paramType == ruleFieldType.MONTHS) ? true : false;

    const disableSave = !hasUnsavedChanges || isAdding || isSaving
        || ((!ruleToEdit?.activityId && !ruleToEdit?.bundleId || ruleToEdit?.activityId === 'new' && !presetActivityData)
            || (selectLot && ruleToEdit?.appliesTo === LOT_OPTIONS.SPECIFIC_LOTS && !(!!ruleToEdit?.lotId?.length))
            || (allowClientSelection && !ruleToEdit?.clientId)
            || ruleToEdit?.ruleParams?.length == 0);

    const getTooltipText = () => {
        return !hasUnsavedChanges ? "No changes to save"
            : selectLot && ruleToEdit?.appliesTo === LOT_OPTIONS.SPECIFIC_LOTS && !(!!ruleToEdit?.lotId?.length) ? "Please select at least one lot"
                : allowClientSelection && !ruleToEdit?.clientId ? "Please select a client"
                    : ruleToEdit?.ruleParams?.length == 0 ? "This activity has no rule parameters. Please add them on the Pricing page before being able to configure rules for it" : "";
    }

    return (
        <Paper className={`${classes.listItemWrapper} ${ruleToEdit?.isNew ? classes.listItemWrapperNew : ""}`}>
            <div className={classes.iconsWrapper}>
                <div className={classes.iconWrapper}>{getIcon(ruleToEdit.activityId, ruleToEdit.bundleId)}</div>
            </div>
            <Grid className={classes.ruleWrapper} container spacing={2}>
                <Grid xs={12} item className={classes.titleWrapper} container>
                    {!ruleToEdit?.isNew ?
                        <Grid item container className={classes.title} spacing={1}>
                            <Grid item xs={12}>{getDisplayName()}</Grid>
                            {(ruleToEdit.bundleId || (!ruleToEdit.activityId && presetActivityData)) &&
                                <Grid item xs={12} className={classes.bundleText}>
                                    *Bundle
                                </Grid>
                            }
                            {showLotInfo && ruleToEdit.lotId && <Grid item >at: {clientLots?.find(c => c.id == ruleToEdit.lotId)?.name}</Grid>}
                        </Grid>
                        :
                        <Grid item xs={11}>
                            {presetActivityData ? <></> :
                                <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                    <ActivityTypesField
                                        onChange={onActivityChange}
                                        value={activityDropdownValue}
                                        label="Activity"
                                        includeBundles
                                    />
                                </Authorize>}
                        </Grid>
                    }
                </Grid>

                {allowClientSelection &&
                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                        <Grid xs={12} item>
                            <CustomInput
                                label="Client"
                                elementType="dropdown"
                                value={ruleToEdit?.clientId}
                                values={clientsWithConfigs || []}
                                onChange={val => onFieldChange('clientId', val)}
                                showEmpty
                                required={true}
                            />
                        </Grid>
                    </Authorize>}

                {selectLot &&
                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                        <Grid xs={12} item >
                            <div className={classes.switchWrapper}>
                                <FormControl>
                                    <RadioGroup
                                        value={ruleToEdit?.appliesTo}
                                        onChange={handleRadioChange}
                                        aria-labelledby="lot-radio-group"
                                        name="lot-radio-group"
                                        row
                                    >
                                        <FormControlLabel value={LOT_OPTIONS.ALL_LOTS} control={<Radio />} label="All Lots" />
                                        <FormControlLabel value={LOT_OPTIONS.ALL_TENANT_LOTS} control={<Radio />} label={`All ${tenantName} Lots`} />
                                        <FormControlLabel value={LOT_OPTIONS.SPECIFIC_LOTS} control={<Radio />} label="Specific Lots" />
                                    </RadioGroup>
                                </FormControl>
                                {(showLotDropdown || ruleToEdit?.appliesTo == LOT_OPTIONS.SPECIFIC_LOTS) &&
                                    <div className={classes.lotMultiSelect} >
                                        <DropdownFilter
                                            title={'Select lots'}
                                            identifier={"id"}
                                            values={currentClientLots}
                                            onSelect={(val) => onSelectLots(val)}
                                            value={!!ruleToEdit?.lotId?.length ? [...ruleToEdit?.lotId] : []}
                                            multiple
                                            disabled={allowClientSelection && !ruleToEdit?.clientId}
                                        />
                                    </div>}
                            </div>
                        </Grid>
                    </Authorize>}

                {(ruleToEdit?.activityId || ruleToEdit?.bundleId || presetActivityData) && <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                    <Grid item xs={12}   >
                        <Grid container className={classes.switchWrapper} >
                            <Grid item >
                                <label >
                                    Consider Check In
                                    <Switch
                                        color="primary"
                                        checked={ruleToEdit?.calcOnCheckin}
                                        onChange={(e) => onFieldChange('calcOnCheckin', e.target.checked)}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                </label>
                            </Grid>
                            {(ruleToEdit?.calcOnCheckin === true) &&
                                <Grid item>
                                    <label>
                                        Consider on First Check In
                                        <Switch
                                            color="primary"
                                            checked={ruleToEdit?.calcOnFirstCheckin}
                                            onChange={(e) => onFieldChange('calcOnFirstCheckin', e.target.checked)}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                        />
                                    </label>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </Authorize>
                }
                {(ruleToEdit?.activityId || ruleToEdit?.bundleId || presetActivityData) && <>
                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                        {!hideVendor && <Grid item xs={4}>
                            <label >
                                Preferred Vendor
                                <CustomInput
                                    id="preferredVendorId"
                                    //label={"Preferred Vendor"}
                                    value={ruleToEdit?.preferredVendorId}
                                    onChange={(val) => onFieldChange('preferredVendorId', parseInt(val))}
                                    elementType="dropdown"
                                    values={vendors}
                                    showEmpty={true}
                                />
                            </label>
                        </Grid>}
                        <Grid item xs={4}>
                            <label>
                                Days to Complete By
                                <CustomInput
                                    //label={"Days to Complete By"}
                                    elementType="input"
                                    value={ruleToEdit?.daysToComplete}
                                    onChange={(e) => onFieldChange('daysToComplete', e)}
                                />
                            </label>
                        </Grid>
                    </Authorize>
                </>}

                {(ruleToEdit?.activityId || ruleToEdit?.bundleId || presetActivityData) && effectiveVehicleTypeConfig &&
                    <Grid item xs={4}>
                        {!!vehicleTypes?.length &&
                            <label>
                                Vehicle Type
                                <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                    <DropdownFilter
                                        //title="Vehicle Type"
                                        identifier={"id"}
                                        values={[{ name: 'All vehicle types', id: null }, ...vehicleTypes]}
                                        showEmpty
                                        value={ruleToEdit?.vehicleTypeIdsList ?? (ruleToEdit?.isNew ? [] : [null])}
                                        onSelect={val => onChangeVehicleTypes(val)}
                                        multiple
                                    />
                                </Authorize>
                            </label>
                        }
                    </Grid>
                }
                {showTimeFrameType &&
                    <Grid item xs={4} className={classes.ruleOperand}>
                        <label>
                            Time Frame Type
                            <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                <CustomInput
                                    //label={'Time Frame Type'}
                                    elementType="dropdown"
                                    value={ruleToEdit?.timeFrameType}
                                    values={timeFrameTypeValues}
                                    onChange={val => onFieldChange('timeFrameType', +val)}
                                    showEmpty
                                    shrink
                                />
                            </Authorize>
                        </label>
                    </Grid>
                }

                <Grid item xs={12} container spacing={1} className={classes.params}>
                    {effectiveRuleConfigs?.map((param, index) =>
                        <>
                            <Grid item key={index} >
                                <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                    {ruleFieldTypeConfig[param.paramType]?.component(
                                        ruleToEdit?.ruleParams?.find(p => p?.paramType === param?.paramType && p?.paramSubType === param?.paramSubType),
                                        (val) => onRuleParamChange(val, param?.paramType, param?.paramSubType),
                                        param?.paramSubType
                                    )}
                                </Authorize>
                            </Grid>
                            {index !== (effectiveRuleConfigs?.length - 1) &&
                                <Grid item key={index} className={classes.ruleOperand}>
                                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                        <CustomInput
                                            // label={}
                                            elementType="dropdown"
                                            value={ruleToEdit?.ruleOperand}
                                            values={ruleOperands}
                                            onChange={val => onFieldChange('ruleOperand', parseInt(val))}
                                        />
                                    </Authorize>
                                </Grid>
                            }
                        </>
                    )
                    }
                </Grid>

                {!isNewActivityMode && (
                    <Grid item xs={12} className={classes.btnContainer} style={{ flex: 1, alignContent: 'end' }}>
                        <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.SAVE_CONFIG}>
                            <Button
                                onClick={cancel}
                                className={classes.button}
                                color="primary"
                            >
                                Cancel
                            </Button>
                            {!ruleToEdit?.isNew && <Button
                                onClick={onDelete}
                                className={classes.button}
                                variant="contained"
                                color="primary"
                                disabled={isDeleting}
                            >
                                {(isDeleting) ? "Deleting..." : "Delete"}
                            </Button>}
                            <Tooltip
                                title={getTooltipText()}
                                arrow
                                disableHoverListener={hasUnsavedChanges && !isAdding && !isSaving && !(selectLot && ruleToEdit?.appliesTo === LOT_OPTIONS.SPECIFIC_LOTS && !(!!ruleToEdit?.lotId?.length)) && !(allowClientSelection && !ruleToEdit?.clientId) && !(ruleToEdit?.ruleParams?.length == 0)}
                            >
                                <span>
                                    <Button
                                        onClick={onSave}
                                        className={classes.button}
                                        variant="contained"
                                        color="primary"
                                        disabled={disableSave}
                                    >
                                        {(isAdding || isSaving) ? "Saving..." : "Save"}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Authorize>
                    </Grid>
                )}

                {isNewActivityMode && (
                    <Grid item xs={12} className={classes.btnContainer} style={{ flex: 1, alignContent: 'end' }}>
                        <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.SAVE_CONFIG}>
                            <Button
                                onClick={() => {
                                    if (hasUnsavedChanges && rule.isNew) {
                                        onUnsavedChanges?.(false);
                                        removeRuleFromParent();
                                    } else if (hasUnsavedChanges) {
                                        setRuleToEdit(rule);
                                        setUnsavedChanges(false);
                                    } else {
                                        triggerPrompt({
                                            title: "Remove Rule",
                                            content: "Are you sure you want to remove this rule?",
                                            onConfirm: () => {
                                                removeRuleFromParent();
                                            },
                                            onCancel: () => { },
                                        });
                                    }
                                }}
                                className={classes.button}
                                variant="outlined"
                                color="primary"
                            >
                                {hasUnsavedChanges ? 'Cancel' : 'Remove Rule'}
                            </Button>
                            <Tooltip
                                title={!hasUnsavedChanges ? "No changes to save" : selectLot && ruleToEdit?.appliesTo === LOT_OPTIONS.SPECIFIC_LOTS && !(!!ruleToEdit?.lotId?.length) ? "Please select at least one lot" : allowClientSelection && !ruleToEdit?.clientId ? "Please select a client" : ""}
                                arrow
                                disableHoverListener={hasUnsavedChanges && !(selectLot && ruleToEdit?.appliesTo === LOT_OPTIONS.SPECIFIC_LOTS && !(!!ruleToEdit?.lotId?.length)) && !(allowClientSelection && !ruleToEdit?.clientId)}
                            >
                                <span>
                                    <Button
                                        onClick={() => {
                                            // Save rule to parent and mark as complete
                                            const updatedRule = { ...ruleToEdit, isNew: false };
                                            saveRuleToParent(updatedRule);
                                            setRuleToEdit(updatedRule);
                                            setUnsavedChanges(false);
                                        }}
                                        className={classes.button}
                                        variant="contained"
                                        color="primary"
                                        disabled={!hasUnsavedChanges || (selectLot && ruleToEdit?.appliesTo === LOT_OPTIONS.SPECIFIC_LOTS && !(!!ruleToEdit?.lotId?.length)) || (allowClientSelection && !ruleToEdit?.clientId)}
                                    >
                                        Done
                                    </Button>
                                </span>
                            </Tooltip>
                        </Authorize>
                    </Grid>
                )}
            </Grid>
        </Paper>
    )
}

export default Rule;